// src/pages/statistiche.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  ArcElement, // per la Pie
  RadialLinearScale, // per il Radar
  Filler, // riempimento Radar
} from "chart.js";

import { useAuth } from "../context/AuthContext";

import RilevatoriView from "../components/statistiche/RilevatoriView";
import MappeView from "../components/statistiche/MappeView";
import SeniorView from "../components/statistiche/SeniorView";
import "./statistiche.css";

/* ========== Chart.js base (una sola volta) ========== */
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
  Title
);

ChartJS.defaults.font.family =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial';
ChartJS.defaults.color = "#e5e7eb";

/* ========== util date ========== */
const pad = (n) => (n < 10 ? "0" + n : "" + n);
const fmtDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// oggi (una sola volta)
const TODAY = new Date();

// data N giorni fa (escluso oggi: 29 giorni fa → 30 giorni di range inclusivo)
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};


/* ========== permessi ========== */
const normalizeRole = (r) =>
  String(r || "").trim().toLowerCase().replace(/_/g, " ");
const canSeeStats = (user) => {
  const role =
    normalizeRole(user?.role_code || user?.ruolo || user?.role);
  return role === "admin" || role === "formatore";
};

/* ========== costanti di pagina ========== */

// zone reali del tuo gestionale (stesso ordine di AgendaPro)
const ZONE_OPTIONS = [
  { id: "all", label: "Tutte le zone" },
  { id: 1, label: "Verona" },
  { id: 2, label: "Modena" },
  { id: 3, label: "Novara" },
  { id: 4, label: "Milano Sud" },
  { id: 5, label: "Milano Nord" },
];

// viste principali
const VIEWS = [
  {
    id: "rilevatori",
    label: "Rilevatori",
    icon: "📍",
    description: "Produzione e qualità degli appuntamenti",
    wip: false,
  },
  {
    id: "senior",
    label: "Senior",
    icon: "✅",
    description: "Chiusure, finanziati, km e fatturato",
    wip: true,
  },
  {
    id: "zone",
    label: "Zone",
    icon: "🗺️",
    description: "Appuntamenti, annullati e fatturato per zona",
    wip: true,
  },
  {
    id: "mappe",
    label: "Mappe",
    icon: "🔥",
    description: "Zone calde/fredde per contratti e fatturato",
    wip: false, // ATTIVA, niente badge WIP
  },
];

const API_STATS = "/preventivi/statistiche/dashboard.php";
const API_USERS = "/preventivi/api";

/* ======================================================
 *               COMPONENTE PRINCIPALE
 * ==================================================== */
export default function Statistiche() {
  const { user } = useAuth();
  const allowed = canSeeStats(user);

  /* ---------- stato di pagina ---------- */
  const [view, setView] = useState("rilevatori");

// default: ultimi 30 giorni (da 29 giorni fa ad oggi)
const [dateFrom, setDateFrom] = useState(fmtDate(daysAgo(29)));
const [dateTo, setDateTo] = useState(fmtDate(TODAY));


  const [zonaId, setZonaId] = useState("all");
  const [rilevatoreId, setRilevatoreId] = useState("all");

  // quando cambio zona, resetto il filtro rilevatore
  useEffect(() => {
    setRilevatoreId("all");
  }, [zonaId]);

  // lista di utenti/rilevatori per il select in alto
  const [rilevatoriList, setRilevatoriList] = useState([]);

  /* ---------- fetch lista rilevatori per la zona ---------- */
  useEffect(() => {
    if (!allowed) return;

    let alive = true;

    const fetchUsers = async () => {
      try {
        const zid = zonaId === "all" ? "" : zonaId;
        const url = `${API_USERS}/users_list.php${
          zid ? `?zona_id=${encodeURIComponent(zid)}` : ""
        }`;

        const r = await fetch(url, { credentials: "same-origin" });
        const j = await r.json().catch(() => ({}));

        let rows =
          Array.isArray(j?.rows) ? j.rows :
          Array.isArray(j?.utenti) ? j.utenti :
          Array.isArray(j) ? j : [];

        const normRoleLocal = (u) =>
          normalizeRole(u.ruolo || u.role || u.ruolo_codice);

        // considero rilevatori, senior, responsabili di zona
        rows = rows.filter((u) => {
          const rr = normRoleLocal(u);
          return (
            rr.includes("rilevatore") ||
            rr.includes("ril ") ||
            rr.includes("rilievo") ||
            rr.includes("junior") ||
            rr.includes("tecnico") ||
            rr.includes("field") ||
            rr.includes("senior") ||
            rr.includes("responsabile")
          );
        });

        const mapped = rows
          .map((u) => ({
            id: String(u.id ?? u.user_id ?? u.ID ?? ""),
            nome:
              u.nome ??
              u.name ??
              u.fullname ??
              u.username ??
              u.email ??
              "",
          }))
          .filter((u) => u.id && u.nome)
          .sort((a, b) => a.nome.localeCompare(b.nome, "it"));

        if (alive) setRilevatoriList(mapped);
      } catch (err) {
        if (alive) setRilevatoriList([]);
      }
    };

    fetchUsers();
    return () => {
      alive = false;
    };
  }, [allowed, zonaId]);

  const rilevatoreOptions = useMemo(
    () => [
      { id: "all", nome: "Tutti i rilevatori" },
      ...rilevatoriList,
    ],
    [rilevatoriList]
  );

  /* ---------- stato dati dashboard ---------- */
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  /* ---------- chiamata a /statistiche/dashboard.php ---------- */
  useEffect(() => {
    if (!allowed) return;

    let aborted = false;

    const payload = {
      view,
      dateFrom,
      dateTo,
      filters: {},
    };

    // filtro zona
    if (zonaId !== "all") {
      payload.filters.zonaId = Number(zonaId);
    }

    // filtro rilevatore solo in vista "rilevatori"
    if (view === "rilevatori" && rilevatoreId !== "all") {
      payload.filters.rilevatoreId = Number(rilevatoreId);
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        setErr("");
        setData(null);

        const r = await fetch(`${API_STATS}?t=${Date.now()}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          cache: "no-store",
          body: JSON.stringify(payload),
        });

        const text = await r.text();
        let j = {};
        try {
          j = text ? JSON.parse(text) : {};
        } catch (e) {
          throw new Error(
            `Risposta non-JSON (HTTP ${r.status}): ${text.slice(0, 400)}`
          );
        }

        if (!r.ok || j.status !== "OK") {
          throw new Error(j.message || `HTTP ${r.status}`);
        }

        if (!aborted) {
          setData(j);
        }
      } catch (e) {
        if (!aborted) {
          setErr(String(e?.message || e));
        }
      } finally {
        if (!aborted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      aborted = true;
    };
  }, [allowed, view, dateFrom, dateTo, zonaId, rilevatoreId]);

  if (!allowed) {
    return <div className="page-pad">Solo admin o formatore.</div>;
  }

  const currentConfig =
    VIEWS.find((v) => v.id === view) || VIEWS[0];

  /* ======================================================
   *                       RENDER
   * ==================================================== */
  return (
    <div className="page-pad stats-page">
      {/* HEADER */}
      <div className="stats-header">
        <div>
          <h1>Statistiche &amp; Analytics</h1>
          <p className="stats-subtitle">
            Con un click passi da rilevatori, senior, zone e mappe.
            Ogni sezione ha i suoi numeri e i suoi grafici.
          </p>
        </div>
      </div>

      {/* SELETTORE VISTE (pilloline) */}
      <div className="stats-view-toggle">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={
              "stats-view-pill" + (view === v.id ? " stats-view-pill--active" : "")
            }
            onClick={() => setView(v.id)}
          >
            <div className="stats-view-pill-main">
              <span className="pill-icon">{v.icon}</span>
              <span className="pill-title">
                {v.label}
                {v.wip && <span className="pill-badge">WIP</span>}
              </span>
            </div>
            <span className="pill-sub">{v.description}</span>
          </button>
        ))}
      </div>

      {/* FILTRI COMUNI + ZONA + RILEVATORE */}
      <div className="stats-filters-card">
        <div className="controls">
          <div className="col-span-2">
            <label>Dal</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="col-span-2">
            <label>Al</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="col-span-3">
            <label>Zona</label>
            <select
              value={zonaId}
              onChange={(e) => setZonaId(e.target.value)}
            >
              {ZONE_OPTIONS.map((z) => (
                <option key={z.id} value={String(z.id)}>
                  {z.label}
                </option>
              ))}
            </select>
          </div>

          {view === "rilevatori" && (
            <div className="col-span-3">
              <label>Rilevatore</label>
              <select
                value={rilevatoreId}
                onChange={(e) => setRilevatoreId(e.target.value)}
              >
                {rilevatoreOptions.map((r) => (
                  <option key={r.id} value={String(r.id)}>
                    {r.nome}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Stato caricamento / errore */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {loading && <span className="chip">Caricamento…</span>}
        {err && <span className="chip chip-warn">Errore: {err}</span>}
        {!loading && !err && data && (
          <span className="chip chip-ok">
            Vista: {currentConfig?.label} · Periodo{" "}
            {dateFrom || "—"} → {dateTo || "—"}
          </span>
        )}
      </div>

      {/* CONTENUTO DELLE SEZIONI */}
      {view === "rilevatori" && (
        <RilevatoriView data={data} loading={loading} />
      )}

      {view === "senior" && (
        <SeniorView data={data} loading={loading} />
      )}

      {view === "zone" && (
        <div style={{ marginTop: 16 }}>
          <div className="chip">
            La vista <b>Zone</b> è in costruzione. Per ora usa Rilevatori e
            Senior per analizzare le performance. 🙂
          </div>
        </div>
      )}

      {view === "mappe" && (
        <div style={{ marginTop: 16 }}>
          <MappeView data={data} loading={loading} />
        </div>
      )}


    </div>
  );
}
